import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Card,
  Typography,
  Stack,
  useTheme,
  alpha,
  Button,
  Rating,
  Divider,
  Grid,
  Avatar,
} from '@mui/material';
import { ArrowBack, Star, RateReview } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getTutorByUser, clearTutor } from '../../state/slices/tutor';

function TutorReviewsPage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user).user;
  const tutorState = useSelector((state) => state.tutor);

  useEffect(() => {
    if (userState && userState.roles.includes('TUTOR') && !tutorState.tutor && !tutorState.loading) {
      dispatch(getTutorByUser(userState.id));
    }
  }, [userState, dispatch, tutorState.tutor, tutorState.loading]);

  const tutor = tutorState.tutor;
  const reviews = tutor?.reviews || [];
  const reviewsCount = Array.isArray(reviews) ? reviews.length : (tutor?.totalReviews || 0);
  const averageRating = tutor?.ratings || 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/tutor-profile')}
          sx={{ mb: 3, color: theme.palette.primary.main }}
        >
          {t('common.back')}
        </Button>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            p: 4,
            background: 'white',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <RateReview sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {t('tutorProfileDashboard.reviews')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Rating value={averageRating} readOnly size="small" precision={0.1} />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  ({averageRating.toFixed(1)}) {reviewsCount} {reviewsCount === 1 ? t('tutorProfileDashboard.review') : t('tutorProfileDashboard.reviews')}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {reviewsCount === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Star sx={{ fontSize: 64, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                {t('tutorProfileDashboard.noReviewsYet')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('tutorProfileDashboard.checkBackAfterFirstSession')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={3}>
              {reviews.map((review, index) => (
                <Card
                  key={review._id || review.id || index}
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 40,
                            height: 40,
                          }}
                        >
                          {review.userId?.firstName?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                            {review.userId?.firstName || 'Unknown'} {review.userId?.lastName || ''}
                          </Typography>
                          {review.rating && (
                            <Rating value={review.rating} readOnly size="small" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 7 }}>
                        {review.comment || review.review || t('tutorProfileDashboard.noComment')}
                      </Typography>
                      {review.createdAt && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 7, display: 'block', mt: 1 }}>
                          {new Date(review.createdAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Stack>
          )}
        </Card>
      </Container>
    </Box>
  );
}

export default TutorReviewsPage;

